import { QuestionCircleOutlined } from '@ant-design/icons';
import { Timestamp } from '@bufbuild/protobuf';
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router';

import { DiagnocoinBatch } from 'protocol/api/billing_new/dto_diagnocoin_new_pb';
import { FeatureFlag } from 'protocol/model/dto_organization_pb';

import { LONG_DATE_FORMAT, PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { DisabledActionTooltip, TextLink } from '@/shared/ui';

import { authModel } from '@/entities/auth';
import { billingModel } from '@/entities/billing';
import {
  getBillingProductBlockReason,
  organizationModel,
} from '@/entities/organization';

import {
  canAdjustDiagnocoinBatch,
  DiagnocoinAdjustmentModal,
} from '@/features/diagnocoinAdjustmentModal';

import {
  addDiagnocoinsBlockReasonMessages,
  DIAGNOCOIN_EXPIRY_HINT_MONTHS,
  diagnocoinsMessages,
} from '../../config';
import { DiagnocoinHistoryModal } from '../diagnocoinHistoryModal/DiagnocoinHistoryModal';

const { Title, Text } = Typography;

const selectAccountInfoLoadingState =
  billingModel.selectors.getLoadingStateSelector('accountInfo');

const selectBatchesLoadingState =
  billingModel.selectors.getLoadingStateSelector('diagnocoinBatches');

type DiagnocoinsProps = {
  organizationID: string;
};

export const Diagnocoins = (props: DiagnocoinsProps) => {
  const { organizationID } = props;

  const { formatMessage, formatDate } = useIntl();

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [batchIDToAdjust, setBatchIDToAdjust] = useState<string>();

  const isEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlagEnabled(
      FeatureFlag.FeatureFlag_Enable_Diagnocoins,
    ),
  );

  const accountInfoLoadingState = useAppSelector(selectAccountInfoLoadingState);
  const batchesLoadingState = useAppSelector(selectBatchesLoadingState);
  const accountInfo = useAppSelector(billingModel.selectors.selectAccountInfo);
  const available = useAppSelector(
    billingModel.selectors.selectDiagnocoinAvailable,
  );
  const reserved = useAppSelector(
    billingModel.selectors.selectDiagnocoinReserved,
  );
  const hasWallet = useAppSelector(
    billingModel.selectors.selectHasDiagnocoinWallet,
  );
  const batches = useAppSelector(
    billingModel.selectors.selectDiagnocoinBatchesNewestFirst,
  );
  const isAutoUseEnabled = useAppSelector(
    billingModel.selectors.selectAutoUseDiagnocoins,
  );
  const organization = useAppSelector(
    organizationModel.selectors.selectOrganization,
  );
  // Adjustments need BasicAccess only, not the org's BillingCanEditProducts.
  const hasAdminBasicAccess = useAppSelector(
    authModel.selectors.selectHasAdminBasicAccess,
  );

  const addDiagnocoinsPath = generatePath(PATHS.addDiagnocoins, {
    organizationID,
  });
  const addDiagnocoinsBlockReason = getBillingProductBlockReason({
    canEditProducts: Boolean(
      organization?.YourPermissions?.BillingCanEditProducts,
    ),
  });

  useEffect(() => {
    setIsHistoryOpen(false);
    setBatchIDToAdjust(undefined);
  }, [accountInfo.ID]);

  const hasCoinHistory = hasWallet || batches.length > 0;
  const batchToAdjust = batches.find((batch) => batch.ID === batchIDToAdjust);

  const expiryHintThreshold = new Date();
  expiryHintThreshold.setMonth(
    expiryHintThreshold.getMonth() + DIAGNOCOIN_EXPIRY_HINT_MONTHS,
  );

  const formatBatchDate = (timestamp?: Timestamp) =>
    timestamp ? formatDate(timestamp.toDate(), LONG_DATE_FORMAT) : '—';

  const now = new Date();

  const batchColumns: ColumnsType<DiagnocoinBatch> = [
    {
      title: formatMessage(diagnocoinsMessages.batchPurchased),
      key: 'purchased',
      render: (_: unknown, batch: DiagnocoinBatch) =>
        formatBatchDate(batch.PurchasedAt),
    },
    {
      title: formatMessage(diagnocoinsMessages.batchTotal),
      key: 'total',
      render: (_: unknown, batch: DiagnocoinBatch) => Number(batch.Total ?? 0),
    },
    {
      title: formatMessage(diagnocoinsMessages.batchRemaining),
      key: 'remaining',
      render: (_: unknown, batch: DiagnocoinBatch) =>
        Number(batch.Remaining ?? 0),
    },
    {
      title: formatMessage(diagnocoinsMessages.batchExpires),
      key: 'expires',
      render: (_: unknown, batch: DiagnocoinBatch) => {
        const expiresAt = batch.ExpiresAt?.toDate();
        const isExpiringSoon =
          Number(batch.Remaining ?? 0) > 0 &&
          expiresAt !== undefined &&
          expiresAt <= expiryHintThreshold;

        return (
          <Text type={isExpiringSoon ? 'warning' : undefined}>
            {formatBatchDate(batch.ExpiresAt)}
          </Text>
        );
      },
    },
    ...(hasAdminBasicAccess
      ? [
          {
            key: 'actions',
            render: (_: unknown, batch: DiagnocoinBatch) =>
              canAdjustDiagnocoinBatch(batch, now) && (
                <Button
                  danger
                  type="link"
                  size="small"
                  onClick={() => setBatchIDToAdjust(batch.ID)}
                >
                  {formatMessage(diagnocoinsMessages.subtract)}
                </Button>
              ),
          },
        ]
      : []),
  ];

  if (!isEnabled) {
    return null;
  }

  return (
    <Card
      style={{ height: '100%' }}
      title={
        <Title level={3} style={{ marginBottom: 0 }}>
          <FormattedMessage
            id="diagnocoins.title"
            defaultMessage="Diagnocoins"
          />
        </Title>
      }
      bordered={false}
      extra={
        addDiagnocoinsBlockReason ? (
          <DisabledActionTooltip
            title={formatMessage(
              addDiagnocoinsBlockReasonMessages[addDiagnocoinsBlockReason],
            )}
          >
            <TextLink to={addDiagnocoinsPath} disabled>
              <FormattedMessage id="addOns.add" defaultMessage="Add" />
            </TextLink>
          </DisabledActionTooltip>
        ) : (
          <TextLink to={addDiagnocoinsPath}>
            <FormattedMessage id="addOns.add" defaultMessage="Add" />
          </TextLink>
        )
      }
      loading={accountInfoLoadingState !== 'succeeded'}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space direction="horizontal" size="large">
          <Statistic
            title={
              <FormattedMessage
                id="diagnocoins.available"
                defaultMessage="Available"
              />
            }
            value={available}
          />

          {reserved > 0 && (
            <Statistic
              title={
                <FormattedMessage
                  id="diagnocoins.reserved"
                  defaultMessage="Held by reports in progress"
                />
              }
              value={reserved}
            />
          )}
        </Space>

        <Descriptions column={1} size="small">
          <Descriptions.Item
            label={
              <Space size={4}>
                {formatMessage(diagnocoinsMessages.autoDeductionTitle)}

                <Tooltip
                  title={formatMessage(
                    diagnocoinsMessages.autoDeductionAdminHint,
                  )}
                >
                  <QuestionCircleOutlined rev={undefined} />
                </Tooltip>
              </Space>
            }
          >
            <Tag color={isAutoUseEnabled ? 'green' : 'default'}>
              {isAutoUseEnabled ? (
                <FormattedMessage
                  id="global.enabled"
                  defaultMessage="Enabled"
                />
              ) : (
                <FormattedMessage
                  id="global.disabled"
                  defaultMessage="Disabled"
                />
              )}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        {hasCoinHistory ? (
          <>
            <Title level={5} style={{ marginBottom: 0 }}>
              {formatMessage(diagnocoinsMessages.topUpsTitle)}
            </Title>

            <Table<DiagnocoinBatch>
              rowKey="ID"
              size="small"
              loading={batchesLoadingState === 'pending'}
              columns={batchColumns}
              dataSource={batches}
              pagination={false}
              scroll={{ x: true }}
            />

            <Button
              type="link"
              style={{ paddingLeft: 0 }}
              onClick={() => setIsHistoryOpen(true)}
            >
              {formatMessage(diagnocoinsMessages.viewHistory)}
            </Button>
          </>
        ) : (
          <Empty
            description={
              <Text>
                <FormattedMessage
                  id="diagnocoins.empty"
                  defaultMessage="No Diagnocoins purchased yet"
                />
              </Text>
            }
          />
        )}
      </Space>

      <DiagnocoinHistoryModal
        accountID={accountInfo.ID}
        open={isHistoryOpen}
        onCancel={() => setIsHistoryOpen(false)}
      />

      <DiagnocoinAdjustmentModal
        accountID={accountInfo.ID}
        batch={batchToAdjust}
        onClose={() => setBatchIDToAdjust(undefined)}
      />
    </Card>
  );
};
